import type React from "react";
import { Sparkles } from "lucide-react";
import type { TopPick } from "@frontend/shared/domain/types/top-pick.types";
import { Button } from "@planta-v2/pds-ui/button";
import { UserAppRoutes } from "@shared/constants/user-app-routes.constants";
import { i18n } from "@frontend/i18n";
import { TopPicksListItem } from "@frontend/shared/presentation/components/participant-profile/TopPicksListItem";

interface IsolatedTopPicksGateProps {
  readonly thirdPick: TopPick | undefined;
  readonly isOptingIn: boolean;
  readonly onOptIn: () => void;
  readonly errorMessage: string | null;
}

const TERMS_SENTINEL = "\x00TERMS\x00";
const PRIVACY_SENTINEL = "\x00PRIVACY\x00";

function LegalParagraph(): React.JSX.Element {
  const termsHref = UserAppRoutes.terms();
  const privacyHref = UserAppRoutes.privacy();
  const termsLink = (
    <a
      key="terms"
      data-testid="isolated-top-picks-gate-terms-link"
      href={termsHref}
      target="_blank"
      rel="noopener"
      className="underline"
    >
      {i18n.t("user.directory.topPicks.isolatedGate.termsLabel")}
    </a>
  );
  const privacyLink = (
    <a
      key="privacy"
      data-testid="isolated-top-picks-gate-privacy-link"
      href={privacyHref}
      target="_blank"
      rel="noopener"
      className="underline"
    >
      {i18n.t("user.directory.topPicks.isolatedGate.privacyLabel")}
    </a>
  );

  const raw = i18n.t("user.directory.topPicks.isolatedGate.legal", {
    terms: TERMS_SENTINEL,
    privacy: PRIVACY_SENTINEL,
  });
  const nodes: React.ReactNode[] = [];
  for (const termsChunk of raw.split(TERMS_SENTINEL)) {
    if (nodes.length > 0) nodes.push(termsLink);
    const privacyParts = termsChunk.split(PRIVACY_SENTINEL);
    privacyParts.forEach((part, i) => {
      if (i > 0) nodes.push(privacyLink);
      nodes.push(part);
    });
  }
  return (
    <p className="text-pds-2xs text-muted-foreground leading-relaxed">
      {nodes}
    </p>
  );
}

export function IsolatedTopPicksGate({
  thirdPick,
  isOptingIn,
  onOptIn,
  errorMessage,
}: IsolatedTopPicksGateProps): React.JSX.Element {
  return (
    <div data-testid="isolated-top-picks-gate" className="grid grid-cols-1">
      {thirdPick !== undefined && (
        <div
          aria-hidden="true"
          className="col-start-1 row-start-1 pointer-events-none select-none"
        >
          <TopPicksListItem pick={thirdPick} onClick={(): void => {}} />
        </div>
      )}
      <div className="col-start-1 row-start-1 flex flex-col items-center gap-4 bg-gradient-to-b from-background/40 to-background to-[25%] px-4 pt-20 pb-2">
        <div className="flex w-full flex-col gap-3 rounded-2xl bg-accent p-4">
          <Button
            data-testid="isolated-top-picks-gate-remember-button"
            className="w-full"
            disabled={isOptingIn}
            onClick={onOptIn}
          >
            <Sparkles className="mr-2 h-4 w-4" />
            {i18n.t("user.directory.topPicks.isolatedGate.buttonLabel")}
          </Button>
          <LegalParagraph />
        </div>
        {errorMessage !== null && (
          <p
            className="text-sm text-destructive"
            data-testid="isolated-top-picks-gate-error"
            role="alert"
          >
            {errorMessage}
          </p>
        )}
      </div>
    </div>
  );
}
